/**
 * Data quality: everything the CSV import could not resolve cleanly.
 * Each section is a real finding from the source files, not a hypothetical check.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import {
  Card, Tile, Badge, Tabs, Empty, QueryState, Notice, fmtInt, downloadCsv, AlumniSwitch,
} from '../components/ui'
import { useRelabel, useCode } from '../lib/terms'

export default function DataQuality() {
  const code = useCode()
  // This page hides alumni like every other, and it is the one place the default
  // costs something: a bad record on a past partner unit is still a bad record. So the
  // switch is in the header rather than folded into a panel, and the notice below
  // says what is being held back instead of leaving a clean section to imply a clean
  // archive.
  const [withAlumni, setWithAlumni] = useState(false)
  const query = useApi(
    ['data-quality', withAlumni],
    `data-quality${withAlumni ? '?include_alumni=1' : ''}`
  )
  const [tab, setTab] = useState('mismatch')

  return (
    <PageHeader
      title="Data Quality"
      sub="What the import flagged when normalizing the two source files"
      actions={<AlumniSwitch checked={withAlumni} onChange={setWithAlumni} where="findings" />}
    >
      <QueryState query={query}>
        {(d) => {
          const mismatchRows = d.countryMismatch.reduce((a, r) => a + r.n, 0)
          const dupeRows = d.duplicateDates.reduce((a, r) => a + r.n, 0)

          const TABS = [
            { key: 'mismatch', label: 'Country disagreements', count: d.countryMismatch.length },
            { key: 'orphans', label: 'Codes missing from registry', count: d.orphanPartners.length },
            { key: 'dupes', label: 'Duplicate partner unit + date', count: d.duplicateDates.length },
            { key: 'incomplete', label: 'Incomplete partner units', count: d.incompletePartners.length },
            { key: 'silent', label: 'Never wrote', count: d.partnersWithoutNewsletters.length },
            { key: 'other', label: 'Dates & empty bodies', count: d.implausibleDates.length + d.emptyBodies.length },
          ]

          return (
            <div className="col">
              {d.alumniHidden?.partners ? (
                <Notice tone="info">
                  Alumni records are left out of every section here:{' '}
                  {fmtInt(d.alumniHidden.partners)} records and{' '}
                  {fmtInt(d.alumniHidden.updates)} of their updates are not being
                  checked. Switch them on in the header to include them.
                </Notice>
              ) : null}
              <div className="grid grid-4">
                <Tile label="Country disagreements" value={fmtInt(mismatchRows)} tone="warn" note={`${d.countryMismatch.length} pairings`} />
                <Tile label="Codes not in registry" value={fmtInt(d.orphanPartners.length)} tone="warn" note="placeholder partner units created" />
                <Tile label="Same partner unit, same date" value={fmtInt(dupeRows)} note={`${d.duplicateDates.length} groups`} />
                <Tile label="Partner units with no updates" value={fmtInt(d.partnersWithoutNewsletters.length)} />
              </div>

              <Notice icon="info" title="Nothing here was silently dropped">
                All 5,162 update rows and all 404 registry rows were imported. These findings
                describe ambiguities in the source data that the schema chose to preserve rather
                than resolve.
              </Notice>

              <Card bodyClass="card-body">
                <Tabs active={tab} onChange={setTab} tabs={TABS} />

                <div style={{ marginTop: 14 }}>
                  {tab === 'mismatch' && (
                    <Section
                      explain="An update records a different country from the partner unit's registry entry. Both values are kept — see the country comparison report for why."
                      rows={d.countryMismatch}
                      empty="Every update agrees with its partner unit's country"
                      csv={['country-mismatches.csv', [
                        { key: 'partner_country_name', label: 'Registered in' },
                        { key: 'country_name', label: 'Update says' },
                        { key: 'n', label: 'Updates' },
                        { key: 'partners', label: 'Partner Units' },
                      ]]}
                      head={['Registered in', 'Update says', 'Updates', 'Partner Units']}
                      row={(r, i) => (
                        <tr key={i}>
                          <td>{r.partner_country_name ?? <span className="subtle">none</span>}</td>
                          <td>
                            <Badge tone="warn">{r.country_name ?? 'none'}</Badge>
                          </td>
                          <td className="num">{fmtInt(r.n)}</td>
                          <td className="num">{fmtInt(r.partners)}</td>
                        </tr>
                      )}
                    />
                  )}

                  {tab === 'orphans' && (
                    <Section
                      explain="These S4CA codes appear in the update file but have no row in the registry file. A placeholder partner unit was created for each so the updates are not lost; they carry the 'stub' badge everywhere in the app."
                      rows={d.orphanPartners}
                      empty="Every update code exists in the registry"
                      head={['Code', 'Updates', '']}
                      row={(r) => (
                        <tr key={r.id}>
                          <td>
                            <Link className="mono" to={`/partners/${r.id}`}>
                              {code(r.s4ca_code)}
                            </Link>
                          </td>
                          <td className="num">{fmtInt(r.newsletter_count)}</td>
                          <td>
                            <Link className="btn btn-sm" to={`/partners/${r.id}/edit`}>
                              Complete this record
                            </Link>
                          </td>
                        </tr>
                      )}
                    />
                  )}

                  {tab === 'dupes' && (
                    <Section
                      explain="More than one update from the same partner unit on the same date. These are kept as separate records — they are usually genuinely separate letters, not duplicates to merge."
                      rows={d.duplicateDates}
                      empty="No partner unit has two updates on one date"
                      csv={['duplicate-dates.csv', [
                        { key: 's4ca_code', label: 'S4CA Code' },
                        { key: 'published_on', label: 'Date' },
                        { key: 'n', label: 'Count' },
                        { key: 'ids', label: 'Record ids' },
                      ]]}
                      head={['Partner Unit', 'Date', 'Count', 'Records']}
                      row={(r, i) => (
                        <tr key={i}>
                          <td className="mono">{code(r.s4ca_code)}</td>
                          <td className="mono">{r.published_on}</td>
                          <td className="num">{r.n}</td>
                          <td>
                            <span className="row row-tight">
                              {String(r.ids)
                                .split(',')
                                .map((id) => (
                                  <Link key={id} className="btn btn-sm" to={`/newsletters/${id}`}>
                                    #{id}
                                  </Link>
                                ))}
                            </span>
                          </td>
                        </tr>
                      )}
                    />
                  )}

                  {tab === 'incomplete' && (
                    <Section
                      explain="Registry rows missing a description, a country or an initial year. The source file leaves these blank; they can be filled in from the partner unit editor."
                      rows={d.incompletePartners}
                      empty="Every partner unit record is complete"
                      csv={['incomplete-partners.csv', [
                        { key: 's4ca_code', label: 'S4CA Code' },
                        { key: 'display_name', label: 'Name' },
                        { key: 'no_description', label: 'Missing description' },
                        { key: 'no_country', label: 'Missing country' },
                        { key: 'no_initial_year', label: 'Missing initial year' },
                      ]]}
                      head={['Code', 'Name', 'Missing', '']}
                      row={(r) => (
                        <tr key={r.id}>
                          <td>
                            <Link className="mono" to={`/partners/${r.id}`}>
                              {code(r.s4ca_code)}
                            </Link>
                          </td>
                          <td>{r.display_name}</td>
                          <td>
                            <span className="row row-tight">
                              {r.no_description ? <Badge>description</Badge> : null}
                              {r.no_country ? <Badge tone="warn">country</Badge> : null}
                              {r.no_initial_year ? <Badge>initial year</Badge> : null}
                            </span>
                          </td>
                          <td>
                            <Link className="btn btn-sm" to={`/partners/${r.id}/edit`}>
                              Edit
                            </Link>
                          </td>
                        </tr>
                      )}
                    />
                  )}

                  {tab === 'silent' && (
                    <Section
                      explain="Registry entries with no update at all. Expected for alumni and inactive partner units, but worth reviewing for anyone marked active."
                      rows={d.partnersWithoutNewsletters}
                      empty="Every partner unit has at least one update"
                      csv={['partners-without-newsletters.csv', [
                        { key: 's4ca_code', label: 'S4CA Code' },
                        { key: 'display_name', label: 'Name' },
                        { key: 'country_name', label: 'Country' },
                        { key: 'initial_year', label: 'Initial Year' },
                        { key: 'status', label: 'Status' },
                      ]]}
                      head={['Code', 'Name', 'Country', 'Since', 'Status']}
                      row={(r) => (
                        <tr key={r.partner_id}>
                          <td>
                            <Link className="mono" to={`/partners/${r.partner_id}`}>
                              {code(r.s4ca_code)}
                            </Link>
                          </td>
                          <td>{r.display_name}</td>
                          <td className="small">{r.country_name ?? '—'}</td>
                          <td className="num">{r.initial_year ?? '—'}</td>
                          <td>
                            <Badge tone={r.status === 'active' ? 'warn' : undefined}>{r.status}</Badge>
                          </td>
                        </tr>
                      )}
                    />
                  )}

                  {tab === 'other' && (
                    <div className="col">
                      <Section
                        title="Implausible dates"
                        explain="Dates more than a year in the future or before 2000. The archive legitimately contains near-future dates, so these are informational."
                        rows={d.implausibleDates}
                        empty="Every date is plausible"
                        head={['Date', 'Partner Unit', '']}
                        row={(r) => (
                          <tr key={r.id}>
                            <td className="mono">{r.published_on}</td>
                            <td className="mono small">{code(r.s4ca_code)}</td>
                            <td>
                              <Link className="btn btn-sm" to={`/newsletters/${r.id}`}>
                                Open
                              </Link>
                            </td>
                          </tr>
                        )}
                      />
                      <Section
                        title="Empty update bodies"
                        explain="Records with no text at all. These usually carry only a prayer request."
                        rows={d.emptyBodies}
                        empty="Every update has text"
                        head={['Date', 'Partner Unit', '']}
                        row={(r) => (
                          <tr key={r.id}>
                            <td className="mono">{r.published_on}</td>
                            <td className="mono small">{code(r.s4ca_code)}</td>
                            <td>
                              <Link className="btn btn-sm" to={`/newsletters/${r.id}`}>
                                Open
                              </Link>
                            </td>
                          </tr>
                        )}
                      />
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}

function Section({ title, explain, rows, head, row, empty, csv }) {
  // This page's own headings and explanations, in the installation's term. The rows
  // under them are records and are drawn as they are.
  const t = useRelabel()
  return (
    <div className="col">
      {title && <h3>{t(title)}</h3>}
      <div className="row">
        <p className="small muted" style={{ flex: 1, minWidth: 240 }}>
          {t(explain)}
        </p>
        {csv && rows.length ? (
          <button className="btn btn-sm" onClick={() => downloadCsv(csv[0], rows, csv[1])}>
            ↓ CSV
          </button>
        ) : null}
      </div>
      {!rows.length ? (
        <Empty icon="check" title={empty} />
      ) : (
        <div className="table-wrap">
          <table className="data compact">
            <thead>
              <tr>
                {head.map((h, i) => (
                  /* The right-align test reads the heading as written, not as
                     relabelled: renaming a partner unit must not un-align a column. */
                  <th key={i} className={h === 'Updates' || h === 'Count' || h === 'Partner Units' ? 'right' : undefined}>
                    {t(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{rows.map((r, i) => row(r, i))}</tbody>
          </table>
        </div>
      )}
    </div>
  )
}
